import { useState, useEffect } from "react";
import { KeyRound, CheckCircle2, AlertCircle, Save, RefreshCw, PlugZap } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  getCrmSettings,
  saveCrmSettings,
  testAriregisterConnection,
  type AriregisterTestError,
  type AriregisterTestResult,
  type CrmSettings,
} from "../../../../lib/crmSettings";
import { Btn, StatusPill } from "../../../ui";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Field, Note, Pill, SaveRow, SecretPill } from "../panel/fields";

/**
 * Which sentence explains each failure. Kept as a lookup rather than a chain of
 * ternaries so that a reason the server learns to send and the UI has not
 * learned to phrase yet is a missing key, not a blank line.
 */
const FAIL_KEY: Record<AriregisterTestError, string> = {
  not_configured: "settings.ariregister.testFailNotConfigured",
  auth_failed: "settings.ariregister.testFailAuth",
  soap_fault: "settings.ariregister.testFailFault",
  http_error: "settings.ariregister.testFailHttp",
  timeout: "settings.ariregister.testFailTimeout",
  network: "settings.ariregister.testFailNetwork",
};

/** Settings -> Integrations: the e-Business Register (RIK) contract credentials. */
export function AriregisterCard() {
  const t = useT();
  const [settings, setSettings] = useState<CrmSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<AriregisterTestResult | null>(null);

  useEffect(() => {
    getCrmSettings()
      .then((s) => {
        setSettings(s);
        setUsername(s.ariregisterUsername);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      await saveCrmSettings({ ariregisterUsername: username, ariregisterPassword: password });
      const updated = await getCrmSettings();
      setSettings(updated);
      setPassword("");
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  /**
   * Tests what is on screen, not what is stored: the typed username and, when
   * the operator has entered one, the typed password. An empty password field
   * means "keep the saved one" here exactly as it does when saving, so the
   * server falls back to the stored value.
   */
  async function handleTest() {
    setTesting(true);
    setTest(null);
    try {
      setTest(
        await testAriregisterConnection({
          ariregisterUsername: username,
          ...(password ? { ariregisterPassword: password } : {}),
        }),
      );
    } catch {
      setTest({ ok: false, error: "network" });
    } finally {
      setTesting(false);
    }
  }

  if (loading) return <PanelLoader />;

  const isConfigured = !!(settings?.ariregisterUsername && settings?.ariregisterPasswordSet);

  return (
    <IntegrationCard
      icon={KeyRound}
      title={t("settings.ariregister.title")}
      desc={t("settings.ariregister.desc")}
      status={
        <ConnStatus
          ok={isConfigured}
          okLabel={t("settings.ariregister.statusConfigured")}
          offLabel={t("settings.ariregister.statusNotConfigured")}
        />
      }
    >
      <form onSubmit={handleSave} className="space-y-5">
        {settings?.source === "env" ? (
          <Note tone="warn">{t("settings.ariregister.fromEnv")}</Note>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("settings.ariregister.username")}>
            <Pill value={username} onChange={setUsername} placeholder="kasutajanimi" autoComplete="off" />
          </Field>

          <Field
            label={t("settings.ariregister.password")}
            hint={
              settings?.ariregisterPasswordSet && !password ? t("settings.ariregister.passwordKept") : undefined
            }
          >
            <SecretPill
              value={password}
              onChange={setPassword}
              placeholder={settings?.ariregisterPasswordSet ? t("settings.ariregister.passwordPlaceholder") : ""}
              reveal
              showLabel={t("settings.ariregister.showPass")}
              hideLabel={t("settings.ariregister.hidePass")}
            />
          </Field>
        </div>

        <p className="crm-sub !mt-0 text-[12px]">{t("settings.ariregister.testHint")}</p>

        {test ? <TestReport t={t} test={test} unsavedPassword={!!password} /> : null}

        <SaveRow>
          <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
            {saveState === "saving" ? (
              <RefreshCw className="size-3.5 animate-spin" />
            ) : (
              <Save className="size-3.5" />
            )}
            {saveState === "saving" ? t("settings.ariregister.saving") : t("settings.ariregister.save")}
          </Btn>
          <Btn onClick={handleTest} disabled={testing}>
            {testing ? <RefreshCw className="size-3.5 animate-spin" /> : <PlugZap className="size-3.5" />}
            {testing ? t("settings.ariregister.testing") : t("settings.ariregister.test")}
          </Btn>
          {saveState === "saved" ? (
            <StatusPill tone="ok">{t("settings.ariregister.saved")}</StatusPill>
          ) : null}
          {saveState === "error" ? (
            <StatusPill tone="lt">{t("settings.ariregister.saveError")}</StatusPill>
          ) : null}
        </SaveRow>
      </form>
    </IntegrationCard>
  );
}

/**
 * What came back from the round trip.
 *
 * A block rather than a line beside the buttons, because the interesting part
 * of a failed test is RIK's own wording, and that does not fit on one line
 * next to two buttons. The company name is shown on success for the same
 * reason a receipt is worth more than a tick: it proves data came back, not
 * merely that something answered.
 */
function TestReport({
  t,
  test,
  unsavedPassword,
}: {
  t: (key: string, vars?: Record<string, string | number>) => string;
  test: AriregisterTestResult;
  unsavedPassword: boolean;
}) {
  const Icon = test.ok ? CheckCircle2 : AlertCircle;
  const headline = test.ok ? t("settings.ariregister.testOk") : t(FAIL_KEY[test.error ?? "network"]);

  return (
    <Note tone={test.ok ? "ok" : "bad"} icon={<Icon className="mt-0.5 size-4 shrink-0" />} live>
      <span className="block font-[650]">{headline}</span>

      {test.ok && test.companyName ? (
        <span className="mt-1 block opacity-90">
          {t("settings.ariregister.testCompany", { name: test.companyName })}
        </span>
      ) : null}

      {/* RIK's own wording, whatever it was. The most useful line on a failure
          and the only one we cannot phrase in advance. */}
      {!test.ok && test.detail ? (
        <span className="mt-1 block break-words opacity-90">
          {t("settings.ariregister.testDetail", { detail: test.detail })}
        </span>
      ) : null}

      {test.regCode ? (
        <span className="mt-1 block font-mono text-[11.5px] opacity-70">
          {t("settings.ariregister.testMeta", {
            regCode: test.regCode,
            ms: test.ms ?? 0,
            endpoint: test.endpoint ?? "",
          })}
        </span>
      ) : null}

      {test.ok && unsavedPassword ? (
        <span className="mt-1.5 block opacity-90">{t("settings.ariregister.testUnsaved")}</span>
      ) : null}
    </Note>
  );
}
